import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

export const INTERVALS = [0.1, 1.0, 3.0, 5.0, 7.0, 10.0];

/**
 * AudioPlayer — Web Audio API driven interval-restricted player.
 * Displays ONLY: circular Play button, animated waveform, unlocked-interval label,
 * Replay button, volume, loading/buffering/error states. No filename, title, artist,
 * artwork, or any answer metadata. Fetches a private proxied stream URL served by
 * the backend with a generic 'clip.mp3' content-disposition.
 */
const AudioPlayer = forwardRef(function AudioPlayer(
  { src, startAt = 0, maxInterval = 0.1, onLoaded, hidden = false },
  ref
) {
  const ctxRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);
  const playStartRef = useRef(0);
  const rafRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [muted, setMuted] = useState(false);
  const [err, setErr] = useState("");

  // Load and decode
  useEffect(() => {
    if (hidden || !src) return;
    let cancelled = false;
    async function load() {
      setReady(false); setErr("");
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        ctxRef.current = ctx;
        const res = await fetch(src, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to load audio");
        const arr = await res.arrayBuffer();
        const buf = await ctx.decodeAudioData(arr.slice(0));
        if (cancelled) return;
        bufferRef.current = buf;
        setReady(true);
        onLoaded?.();
      } catch (e) {
        setErr(e.message || "Audio error");
      }
    }
    load();
    return () => {
      cancelled = true;
      stop();
      try { ctxRef.current?.close(); } catch { /* noop */ }
      ctxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, hidden]);

  const stop = useCallback(() => {
    try { sourceRef.current?.stop(); } catch { /* noop */ }
    sourceRef.current = null;
    cancelAnimationFrame(rafRef.current);
    setPlaying(false);
  }, []);

  useImperativeHandle(ref, () => ({ stop }), [stop]);

  const tick = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime - playStartRef.current;
    setElapsed(now);
    if (now >= maxInterval) {
      stop();
      setElapsed(maxInterval);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [maxInterval, stop]);

  const play = useCallback(async () => {
    const ctx = ctxRef.current;
    const buf = bufferRef.current;
    if (!ctx || !buf) return;
    if (ctx.state === "suspended") await ctx.resume();
    stop();

    const src = ctx.createBufferSource();
    const gain = ctx.createGain();
    gain.gain.value = muted ? 0 : volume;
    src.buffer = buf;
    src.connect(gain).connect(ctx.destination);

    const offset = Math.min(startAt, Math.max(0, buf.duration - 0.05));
    const duration = Math.min(maxInterval, Math.max(0.05, buf.duration - offset));
    playStartRef.current = ctx.currentTime;
    src.start(0, offset, duration);
    sourceRef.current = src;
    gainRef.current = gain;
    setPlaying(true);
    setElapsed(0);
    rafRef.current = requestAnimationFrame(tick);
    src.onended = () => setPlaying(false);
  }, [muted, volume, startAt, maxInterval, stop, tick]);

  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.value = muted ? 0 : volume;
  }, [volume, muted]);

  const pct = Math.min(100, (elapsed / maxInterval) * 100);

  if (hidden) return null;

  return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 sm:p-6 shadow-2xl">
      {err && <div className="text-red-400 text-sm mb-3" data-testid="audio-error">{err}</div>}

      {/* Waveform */}
      <div className="flex items-end justify-center gap-1 h-16 mb-4" aria-hidden>
        {Array.from({ length: 32 }).map((_, i) => (
          <span key={i}
            className={`w-1.5 rounded-full ${playing ? "wave-bar bg-red-500" : "bg-slate-700"}`}
            style={{
              height: `${20 + ((i * 13) % 45)}px`,
              animationDelay: `${(i % 8) * 0.08}s`,
            }} />
        ))}
      </div>

      {/* Progress bar */}
      <div className="relative h-2 bg-slate-800 rounded-full overflow-hidden mb-4">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-600 to-red-400"
             style={{ width: `${pct}%` }} data-testid="audio-progress" />
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button size="lg"
          disabled={!ready}
          onClick={playing ? stop : play}
          data-testid="play-audio-btn"
          className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-500 text-white shadow-[0_0_30px_rgba(220,38,38,0.5)] p-0">
          {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
        </Button>

        <div className="flex-1 flex items-center gap-2">
          <button onClick={() => setMuted(!muted)} className="text-slate-400 hover:text-white p-1"
            data-testid="mute-btn" aria-label="Toggle mute">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <Slider
            value={[muted ? 0 : volume * 100]}
            onValueChange={(v) => { setVolume(v[0] / 100); if (v[0] > 0 && muted) setMuted(false); }}
            max={100}
            step={1}
            className="max-w-[140px]"
            data-testid="volume-slider"
          />
        </div>

        <Button variant="outline" size="sm" onClick={play} disabled={!ready || playing}
          data-testid="replay-btn"
          className="border-slate-700 text-slate-200 hover:bg-red-950/40 hover:text-white">
          <RotateCcw className="h-4 w-4 mr-1" /> Replay
        </Button>
      </div>

      <div className="mt-3 text-center">
        <span className="text-xs font-mono uppercase tracking-widest text-red-500">
          {maxInterval < 1 ? maxInterval.toFixed(1) : maxInterval.toFixed(0)}s window
        </span>
        {!ready && !err && (
          <span className="ml-2 text-xs text-slate-500 font-mono" data-testid="audio-loading">buffering…</span>
        )}
      </div>
    </div>
  );
});

export default AudioPlayer;
